import { Currency, Form, SingleDatePicker } from '@mairistem/composants';
import { useUpdateEffect } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import InputNumber from '@/components/Autre/InputNumber';
import type { BonCAFType } from '@/types';

interface BonCAFProps {
  disabled: boolean;
  reste: number;
  dateValidite: string;
  nombre: number;
  montantUnitaire: number;
  onChange: (value: BonCAFType, complet: boolean) => void;
  onError?: (error: string) => void;
}

const BonCAF = (props: BonCAFProps) => {
  const {
    disabled,
    reste,
    dateValidite: dateProps,
    nombre: nombreProps,
    montantUnitaire: montantUnitaireProps,
    onChange,
    onError,
  } = props;

  const [date, setDate] = React.useState<dayjs.Dayjs>(dateProps ? dayjs(dateProps) : null);
  const [nombre, setNombre] = React.useState<number>(nombreProps);
  const [montantUnitaire, setMontantUnitaire] = React.useState<number>(montantUnitaireProps || 0);
  const [total, setTotal] = React.useState<number>(0);

  const handleChange = (value, setter) => {
    setter(value);
  };

  const isComplet = React.useCallback(
    () => !_.isNil(date) && !_.isEmpty(nombre) && montantUnitaire > 0 && total <= reste,
    [date, nombre, montantUnitaire, total, reste],
  );

  useUpdateEffect(() => {
    if (!disabled) {
      onChange(
        {
          ...(date !== null && { dateValidite: date?.format('DD/MM/YYYY') }),
          ...(nombre !== 0 && { nombre }),
          ...(montantUnitaire !== 0 && { montantUnitaire }),
          ...(total !== 0 && { montant: total }),
        },
        isComplet(),
      );
    }
  }, [disabled, date, nombre, montantUnitaire, total, isComplet]);

  useUpdateEffect(() => {
    if (nombre !== 0 && montantUnitaire !== 0) {
      // Arrondi au centime : 3 × 7,10 vaut 21.299999999999997 en flottant.
      const t = parseFloat((nombre * montantUnitaire).toFixed(2));
      if (t > reste && onError) {
        onError('Le total ne peut pas être supérieur au reste');
      }

      setTotal(t);
    }
  }, [nombre, montantUnitaire]);

  return (
    <Form.Group style={{ marginTop: 0, marginBottom: 0, alignItems: 'center' }}>
      <Form.Field inline required>
        <label>Date de validité</label>
        <SingleDatePicker
          data-testid="dateValiditeBonCaf"
          disabled={disabled}
          autoFocus={!disabled}
          clearable
          value={date}
          onChange={(val: dayjs.Dayjs) => setDate(val)}
          disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
        />
      </Form.Field>
      <InputNumber
        data-testid="nombreBonCaf"
        disabled={disabled}
        value={nombre}
        maxLength={2}
        onChange={(value) => handleChange(value, setNombre)}
        required
      />
      <Currency
        data-testid="montantUnitaireBonCaf"
        disabled={disabled}
        value={montantUnitaire}
        label="Montant unitaire"
        inline
        placeholder=""
        style={{ width: 100 }}
        valueMin={0}
        description=""
        onBlur={(e, value) => handleChange(value, setMontantUnitaire)}
        required
      />
      <Currency
        readOnly={!disabled}
        disabled={disabled}
        value={total}
        label="Total"
        inline
        error={!disabled && total > reste}
        placeholder=""
        style={{ width: 100 }}
        valueMin={0}
        valueMax={reste}
        description=""
      />
    </Form.Group>
  );
};

export default BonCAF;
